import EmptyState from 'components/EmptyState';
import { LockIcon } from 'components/icons';
import { useState } from 'react';
import { useGetWidgetGalleryQuery } from 'store/api/homeWidgetSharing';
import { HOME_WIDGET_SUBJECTS, type HomeWidgetSubject } from 'types/homeWidgetSharing';
import type { HomeEntry, HomePlacement } from 'types/homeWidgets';
import { foldForSearch } from 'utils/foldForSearch';
import { t } from 'utils/getTranslations';
import { FORM_LABEL, formIcon, periodLabel } from '../creator/catalogueLabels';
import { isOnHome } from '../grid/layout';
import { FilterPills } from '../share/FilterPills';
import { SearchField } from '../share/SearchField';
import { SUBJECT_LABEL } from '../share/subjects';
import { AddToHome } from './AddToHome';

type Props = {
    /** The layout being arranged, which an added widget joins. */
    entries: HomeEntry[];
    /** Home holds as many entries as it may. */
    full: boolean;
    onAdd: (placement: HomePlacement) => void;
};

/**
 * What the firm published for everyone, searched and narrowed by subject in the browser. A card ends in the button
 * that adds the widget to the layout being arranged, or says it is there already: in the person's own part, by
 * that layout, or in the locked part, where the firm put it and from where it cannot be added a second time.
 */
export function FirmGalleryTab({ entries, full, onAdd }: Props) {
    const { data: gallery, isError, refetch } = useGetWidgetGalleryQuery();
    const [search, setSearch] = useState('');
    const [subject, setSubject] = useState<HomeWidgetSubject | 'ALL'>('ALL');

    if (!gallery) {
        return isError ? (
            <EmptyState
                size="panel"
                variant="error"
                errorTitleId="home.widgets.error.loading-gallery"
                onRetry={refetch}
                surface="home-widget-gallery"
            />
        ) : null;
    }
    if (gallery.length === 0) {
        return <EmptyState size="panel" titleId="home.widgets.gallery.empty" surface="home-widget-gallery" />;
    }

    const wanted = foldForSearch(search.trim());
    const shown = gallery.filter(
        ({ widget, description, subject: filedUnder }) =>
            (subject === 'ALL' || filedUnder === subject) &&
            [widget.name, description, widget.owner.name].some((text) => foldForSearch(text).includes(wanted))
    );

    return (
        <>
            <div className="flex flex-wrap items-center gap-3 px-6 pt-4">
                <SearchField
                    label={t('home.widgets.gallery.search')}
                    placeholder={t('home.widgets.gallery.search-placeholder')}
                    value={search}
                    onChange={setSearch}
                />
                <FilterPills
                    label={t('home.widgets.share.subject')}
                    value={subject}
                    options={[
                        { value: 'ALL', label: t('all') },
                        ...HOME_WIDGET_SUBJECTS.map((value) => ({ value, label: t(SUBJECT_LABEL[value]) })),
                    ]}
                    onChange={setSubject}
                />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-4 pb-6">
                {shown.length === 0 ? (
                    <EmptyState
                        size="panel"
                        variant="filtered"
                        onClearFilters={() => {
                            setSearch('');
                            setSubject('ALL');
                        }}
                        surface="home-widget-gallery"
                    />
                ) : (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
                        {shown.map(({ widget, description, onHome }) => {
                            const placement: HomePlacement = { kind: 'WIDGET', widget };
                            const Icon = formIcon(widget.definition);
                            return (
                                <article
                                    key={widget.id}
                                    className="border-neutral-tint bg-surface-tile flex flex-col gap-2 rounded-md border p-3.5"
                                >
                                    <div className="flex items-center gap-2.5">
                                        <span className="bg-primary-tint text-primary-ink flex size-8 flex-none items-center justify-center rounded-md">
                                            <Icon className="size-4" />
                                        </span>
                                        <div className="flex min-w-0 flex-1 flex-col">
                                            <h3 className="text-body1 truncate leading-[1.43] font-semibold">
                                                {widget.name}
                                            </h3>
                                            <span className="text-muted-foreground text-caption">
                                                {t(FORM_LABEL[widget.definition.form])} ·{' '}
                                                {periodLabel(widget.definition.period)}
                                            </span>
                                        </div>
                                    </div>
                                    <p className="text-foreground-secondary text-meta min-h-[54px] flex-1">
                                        {description}
                                    </p>
                                    <div className="flex min-h-8 items-center gap-2">
                                        <span className="text-muted-foreground text-caption min-w-0 flex-1 truncate">
                                            {t('home.widgets.gallery.by', { name: widget.owner.name })}
                                        </span>
                                        {onHome === 'LOCKED' ? (
                                            <span className="text-foreground-secondary text-meta flex items-center gap-1.5">
                                                <LockIcon className="size-3.5" />
                                                {t('home.widgets.on-home-locked')}
                                            </span>
                                        ) : (
                                            // What ends a card brings the gap over it; here it shares a row.
                                            <div className="-mt-1 flex flex-none">
                                                <AddToHome
                                                    onHome={isOnHome(entries, placement)}
                                                    full={full}
                                                    entries={entries}
                                                    placement={placement}
                                                    onAdd={() => onAdd(placement)}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}
            </div>
        </>
    );
}
